import React, { useEffect, useMemo, useRef, useState } from "react";
import "./DailyInfo.css";
import useGeoLocation from "./hooks/useGeoLocation";

const TAMIL_RAASI_12 = [
  "மேஷம்",
  "ரிஷபம்",
  "மிதுனம்",
  "கடகம்",
  "சிம்மம்",
  "கன்னி",
  "துலாம்",
  "விருச்சிகம்",
  "தனுசு",
  "மகரம்",
  "கும்பம்",
  "மீனம்",
];

const TAMIL_NAKSHATRA_27 = [
  "அசுவினி",
  "பரணி",
  "கார்த்திகை",
  "ரோகிணி",
  "மிருகசீரிடம்",
  "திருவாதிரை",
  "புனர்பூசம்",
  "பூசம்",
  "ஆயில்யம்",
  "மகம்",
  "பூரம்",
  "உத்திரம்",
  "அஸ்தம்",
  "சித்திரை",
  "ஸ்வாதி",
  "விசாகம்",
  "அனுஷம்",
  "கேட்டை",
  "மூலம்",
  "பூராடம்",
  "உத்திராடம்",
  "திருவோணம்",
  "அவிட்டம்",
  "சதயம்",
  "பூரட்டாதி",
  "உத்திரட்டாதி",
  "ரேவதி",
];

// Simple transliteration-style Tamil names for tithi (commonly used in Tamil panchangams).
const TAMIL_TITHI_30 = [
  "பிரதமை",
  "த்விதியை",
  "திரிதியை",
  "சதுர்த்தி",
  "பஞ்சமி",
  "ஷஷ்டி",
  "ஸப்தமி",
  "அஷ்டமி",
  "நவமி",
  "தசமி",
  "ஏகாதசி",
  "த்வாதசி",
  "திரயோதசி",
  "சதுர்தசி",
  "பௌர்ணமி / அமாவாசை",
];

function addDays(d, days) {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

function formatHM(date) {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function formatRange(start, end) {
  if (!start || !end) return "—";
  return `${formatHM(start)} – ${formatHM(end)}`;
}

function computeAbhijit(sunRise, sunSet) {
  if (!sunRise || !sunSet) return null;
  const sr = new Date(sunRise).getTime();
  const ss = new Date(sunSet).getTime();
  if (!Number.isFinite(sr) || !Number.isFinite(ss) || ss <= sr) return null;
  const mid = sr + (ss - sr) / 2;
  // Abhijit muhurta commonly approximated as ±24 minutes around midday.
  const start = new Date(mid - 24 * 60 * 1000);
  const end = new Date(mid + 24 * 60 * 1000);
  return { start, end };
}

function DailyInfo() {
  const { coords, status: geoStatus } = useGeoLocation();
  const panchangRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [data, setData] = useState({
    englishDateLong: "",
    dayEnglish: "",
    dayTamil: "",
    tamilMonth: "",
    tamilDate: null,
    rahuKalam: "",
    yamagandam: "",
    tithi: "",
    paksha: "",
    nakshatra: "",
    raasi: "",
    yoga: "",
    karna: "",
    sunRise: null,
    sunSet: null,
    abhijit: null,
  });

  const daysEnglish = useMemo(
    () => ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    []
  );
  const daysTamil = useMemo(() => ["ஞாயிறு", "திங்கள்", "செவ்வாய்", "புதன்", "வியாழன்", "வெள்ளி", "சனி"], []);

  useEffect(() => {
    // eslint-disable-next-line global-require
    const { MhahPanchang } = require("mhah-panchang");
    const obj = new MhahPanchang();
    obj.setMhahConstant("Raasi", "name", TAMIL_RAASI_12);
    obj.setMhahConstant("Nakshatra", "name", TAMIL_NAKSHATRA_27);
    panchangRef.current = obj;
  }, []);

  // Tamil calendar conversion (month/day) – lightweight approximation used earlier
  const getTamilDateInfo = (inputDate = new Date()) => {
    const d = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());
    const MS_DAY = 24 * 60 * 60 * 1000;

    const months = [
      "சித்திரை",
      "வைகாசி",
      "ஆனி",
      "ஆடி",
      "ஆவணி",
      "புரட்டாசி",
      "ஐப்பசி",
      "கார்த்திகை",
      "மார்கழி",
      "தை",
      "மாசி",
      "பங்குனி",
    ];

    const CUTS = [
      [3, 14],
      [4, 15],
      [5, 15],
      [6, 16],
      [7, 17],
      [8, 17],
      [9, 17],
      [10, 16],
      [11, 16],
      [0, 14],
      [1, 13],
      [2, 14],
    ];

    const gy = d.getFullYear();
    const puthanduThisYear = new Date(gy, 3, 14);
    const startYear = d >= puthanduThisYear ? gy : gy - 1;

    const starts = CUTS.map(([m, day], i) => {
      const year = i <= 8 ? startYear : startYear + 1;
      return new Date(year, m, day);
    });

    let idx = 0;
    for (let i = 0; i < starts.length; i++) {
      if (d >= starts[i]) idx = i;
    }

    const monthStart = starts[idx];
    const dayInMonth = Math.floor((d - monthStart) / MS_DAY) + 1;

    return { monthIndex: idx, monthName: months[idx], day: dayInMonth };
  };

  const rahuTable = useMemo(
    () => ({
      Sunday: "4:30 PM – 6:00 PM",
      Monday: "7:30 AM – 9:00 AM",
      Tuesday: "3:00 PM – 4:30 PM",
      Wednesday: "12:00 PM – 1:30 PM",
      Thursday: "1:30 PM – 3:00 PM",
      Friday: "10:30 AM – 12:00 PM",
      Saturday: "9:00 AM – 10:30 AM",
    }),
    []
  );

  const yamagandamTable = useMemo(
    () => ({
      Sunday: "12:00 PM – 1:30 PM",
      Monday: "10:30 AM – 12:00 PM",
      Tuesday: "9:00 AM – 10:30 AM",
      Wednesday: "7:30 AM – 9:00 AM",
      Thursday: "6:00 AM – 7:30 AM",
      Friday: "3:00 PM – 4:30 PM",
      Saturday: "1:30 PM – 3:00 PM",
    }),
    []
  );

  const quote = useMemo(() => {
    const quotes = [
      "நேர்மை தான் மனிதனின் மிகப் பெரிய பலம்.",
      "அன்பு பேசும் மொழி அனைத்தையும் வெல்லும்.",
      "உழைப்பால் மட்டுமே உயர்வு கிடைக்கும்.",
      "கஷ்டத்திற்கு பின் வரும் வெற்றி இனிமையானது.",
      "வாழ்க்கை ஒரு பாடம்; தினமும் புதியதை கற்றுக்கொள்.",
    ];
    return quotes[Math.floor(Math.random() * quotes.length)];
  }, []);

  useEffect(() => {
    const d = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 12, 0, 0);
    const dayEn = daysEnglish[d.getDay()];
    const tamilDay = daysTamil[d.getDay()];
    const tamilDateInfo = getTamilDateInfo(d);

    const obj = panchangRef.current;
    const hasLoc = Boolean(coords?.latitude && coords?.longitude);

    let cal = null;
    let sun = null;

    if (obj && hasLoc) {
      try {
        cal = obj.calendar(d, coords.latitude, coords.longitude);
      } catch {
        cal = null;
      }
      try {
        sun = obj.sunTimer(d, coords.latitude, coords.longitude);
      } catch {
        sun = null;
      }
    }

    // Tithi: pick Tamil string based on ino if available
    const tithiIno = cal?.Tithi?.ino;
    const tithiName =
      typeof tithiIno === "number" && tithiIno >= 1 && tithiIno <= 30
        ? tithiIno === 15 || tithiIno === 30
          ? TAMIL_TITHI_30[14]
          : TAMIL_TITHI_30[(tithiIno - 1) % 15]
        : cal?.Tithi?.name_en_IN || cal?.Tithi?.name || "—";

    const pakshaName = cal?.Paksha?.name_en_IN ? (cal.Paksha.name_en_IN === "Shukla" ? "சுக்ல" : "கிருஷ்ண") : "—";

    const abhijit = computeAbhijit(sun?.sunRise, sun?.sunSet);

    setData({
      englishDateLong: d.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
      dayEnglish: dayEn,
      dayTamil: tamilDay,
      tamilMonth: tamilDateInfo.monthName,
      tamilDate: tamilDateInfo.day,
      rahuKalam: rahuTable[dayEn] || "—",
      yamagandam: yamagandamTable[dayEn] || "—",
      tithi: tithiName,
      paksha: pakshaName,
      nakshatra: cal?.Nakshatra?.name || "—",
      raasi: cal?.Raasi?.name || "—",
      yoga: cal?.Yoga?.name_en_IN || cal?.Yoga?.name || "—",
      karna: cal?.Karna?.name_en_IN || cal?.Karna?.name || "—",
      sunRise: sun?.sunRise || null,
      sunSet: sun?.sunSet || null,
      abhijit,
    });
  }, [selectedDate, coords, daysEnglish, daysTamil, rahuTable, yamagandamTable]);

  return (
    <div className="dailyPaper" aria-label="Daily Tamil calendar paper">
      <div className="dailyPaper__top">
        <button
          type="button"
          className="dailyPaper__navBtn"
          aria-label="Previous day"
          onClick={() => setSelectedDate((d) => addDays(d, -1))}
        >
          ‹
        </button>

        <div className="dailyPaper__head">
          <div className="dailyPaper__title">தினசரி நாள்காட்டி</div>
          <div className="dailyPaper__dateLine">
            {data.englishDateLong} • {data.dayTamil} • {data.tamilDate ? `${data.tamilDate} ${data.tamilMonth}` : "—"}
          </div>
        </div>

        <button
          type="button"
          className="dailyPaper__navBtn"
          aria-label="Next day"
          onClick={() => setSelectedDate((d) => addDays(d, 1))}
        >
          ›
        </button>
      </div>

      <div className="dailyPaper__grid">
        <div className="dailyPaper__section">
          <div className="dailyPaper__sectionTitle">பஞ்சாங்கம்</div>
          <div className="dailyPaper__rows">
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">திதி</div>
              <div className="dailyPaper__val">{data.tithi}</div>
            </div>
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">பக்ஷம்</div>
              <div className="dailyPaper__val">{data.paksha}</div>
            </div>
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">நட்சத்திரம்</div>
              <div className="dailyPaper__val">{data.nakshatra}</div>
            </div>
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">ராசி</div>
              <div className="dailyPaper__val">{data.raasi}</div>
            </div>
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">யோகம்</div>
              <div className="dailyPaper__val">{data.yoga}</div>
            </div>
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">கரணம்</div>
              <div className="dailyPaper__val">{data.karna}</div>
            </div>
          </div>
        </div>

        <div className="dailyPaper__section">
          <div className="dailyPaper__sectionTitle">நாள்நேரம்</div>
          <div className="dailyPaper__rows">
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">சூரியோதயம்</div>
              <div className="dailyPaper__val">{formatHM(data.sunRise)}</div>
            </div>
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">சூரியாஸ்தமனம்</div>
              <div className="dailyPaper__val">{formatHM(data.sunSet)}</div>
            </div>
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">நல்ல நேரம்</div>
              <div className="dailyPaper__val">
                {data.abhijit ? formatRange(data.abhijit.start, data.abhijit.end) : "—"}
                <span className="dailyPaper__footerNote"> (அபிஜித் முகூர்த்தம்)</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dailyPaper__section">
          <div className="dailyPaper__sectionTitle">தோஷ நேரங்கள்</div>
          <div className="dailyPaper__rows">
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">ராகு காலம்</div>
              <div className="dailyPaper__val">{data.rahuKalam}</div>
            </div>
            <div className="dailyPaper__row">
              <div className="dailyPaper__key">யமகண்டம்</div>
              <div className="dailyPaper__val">{data.yamagandam}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="dailyPaper__footerNote">
        {geoStatus.state === "requesting" && <span className="dailyPaper__badge">இடம் பெறுகிறது…</span>}
        {geoStatus.state === "granted" && <span className="dailyPaper__badge">GPS இடம்</span>}
        {geoStatus.state === "fallback" && <span className="dailyPaper__badge">Chennai (fallback)</span>}
        <span style={{ marginLeft: 8 }} className="dailyPaper__badge">
          {quote}
        </span>
      </div>
    </div>
  );
}

export default DailyInfo;
